import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { ThemeProvider } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import loginBg from '@/images/login_bg.png';
import { apiClient } from '@/api/axiosClient';
import { useLocale } from '@/i18n/useLocale';
import { createAppTheme } from '@/theme';
import { errorMessageFrom } from '@/lib/toast';

function buildSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().min(1, t('validation.emailRequired')).email(t('validation.emailInvalid')),
  });
}

type ForgotPasswordFormValues = z.infer<ReturnType<typeof buildSchema>>;

const FIELD_RADIUS = '10px';
const PILL_RADIUS = '999px';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const { direction } = useLocale();
  const cardTheme = createAppTheme('light', direction);

  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const schema = buildSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setFormError(null);
    try {
      await apiClient.post('/auth/forgot-password', { email: values.email });
      setSubmitted(true);
    } catch (error) {
      setFormError(errorMessageFrom(error));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundImage: `url(${loginBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <ThemeProvider theme={cardTheme}>
        <Paper elevation={8} sx={{ width: '100%', maxWidth: 400, borderRadius: '20px', p: 5, bgcolor: '#fff' }}>
          {submitted ? (
            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {t('forgotPasswordPage.checkEmailTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('forgotPasswordPage.checkEmailSubtitle')}
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                fullWidth
                sx={{ borderRadius: PILL_RADIUS, py: 1.25, mt: 1 }}
              >
                {t('forgotPasswordPage.backToLogin')}
              </Button>
            </Stack>
          ) : (
            <>
              <Stack alignItems="center" spacing={0.75} sx={{ mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {t('forgotPasswordPage.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('forgotPasswordPage.subtitle')}
                </Typography>
                <Box sx={{ width: 40, height: 3, borderRadius: '2px', bgcolor: 'primary.main', mt: 1 }} />
              </Stack>

              <Box component="form" onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                <Stack spacing={2.5}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('forgotPasswordPage.emailPlaceholder')}
                        error={Boolean(errors.email)}
                        helperText={errors.email?.message}
                        fullWidth
                        autoFocus
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlinedIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            sx: { borderRadius: FIELD_RADIUS },
                          },
                        }}
                      />
                    )}
                  />

                  {formError ? (
                    <Typography variant="body2" color="error">
                      {formError}
                    </Typography>
                  ) : undefined}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ borderRadius: PILL_RADIUS, py: 1.25 }}
                  >
                    {isSubmitting ? t('forgotPasswordPage.sending') : t('forgotPasswordPage.submit')}
                  </Button>

                  <Link
                    component={RouterLink}
                    to="/login"
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                    {t('forgotPasswordPage.backToLogin')}
                  </Link>
                </Stack>
              </Box>
            </>
          )}

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 4 }}>
            <GppGoodOutlinedIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary" align="center">
              {t('footer')}
            </Typography>
          </Stack>
        </Paper>
      </ThemeProvider>
    </Box>
  );
}
